import { action, observable } from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage } from 'react-intl';
import React from 'react';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { alertServerError } from '../../../../global/modal/alerts-host/alert-templates';
import { IEventLinkDTO, IEventLinksDAO, IEventLinkType } from '../../../../../api/event-links.dao';
import { IEventRequestParams } from '../../../../../api/shared.dto';
import { Space } from '../../../../../constants/html-character-entities';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';

export type CreateEventLinkType = Omit<IEventLinkDTO, 'linkId'>;

export class EventLinksViewModel {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    public get eventLinks(): IEventLinkDTO[] {
        return this.links || [];
    }

    public async initialize(): Promise<void> {
        await this.initLinkTypes();
        await this.initEventLinks();
    }

    public get linkTypes(): IEventLinkType[] {
        return this.eventLinkTypes || [];
    }

    public get passportSubdomain(): string | null {
        return this.subdomain;
    }

    @action.bound
    public async onAddEventLink(link: CreateEventLinkType): Promise<void> {
        const dto = { ...link };
        dto.label = link.label.trim();
        return this.createEventLink(dto);
    }

    @action.bound
    public async onEditEventLink(linkId: number, link: CreateEventLinkType, props: IEventLinkDTO): Promise<void> {
        const currentUrl = window.location.href;
        this.trackingService.get().track(TRACKING_EVENTS.EDIT_LINKS, {
            'Type value': link.type,
            'Type': convertBoolTypeForMixPanel(props.type !== link.type),
            'URL': convertBoolTypeForMixPanel(props.url !== link.url),
            'Label': convertBoolTypeForMixPanel(props.label !== link.label),
            'Account Name': this.globalStorage.getEvent()?.accountName,
            'Event ID': this.eventParams.eventId,
            'Event link:': currentUrl
        });

        const dto = { ...link };
        dto.label = link.label.trim();
        return this.editEventLink(linkId, dto);
    }

    constructor(
        props,
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly eventLinksDAO: IEventLinksDAO = IoC.Container.get(IEventLinksDAO),
        @IoC.Inject
        private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
    ) {
        this.eventParams = { accountId: props.accountId, eventId: props.eventId };
        this.setEventLinks(props.links);
        this.subdomain = props.subdomain || '';
        this.onCancel = props.onCancel;
        this.isEventTrackerForm = props.isEventTrackerForm;
        if (props.isEventTrackerForm) {
            this.fetchEventsList = props.fetchEventsList;
            this.closeAddLinksPopup = () => {
                props.closeAddLinksPopup?.();
                this.trackCancelEventLink();
            };
        }
    }

    @action.bound
    private setEventLinks(links: IEventLinkDTO[]): void {
        if (Array.isArray(links)) this.links = links;
    }

    @action.bound
    private setEventLinkTypes(eventLinkTypes: IEventLinkType[]): void {
        if (Array.isArray(eventLinkTypes)) this.eventLinkTypes = eventLinkTypes;
    }

    @observable
    private links: IEventLinkDTO[];
    @observable
    private eventLinkTypes: IEventLinkType[];
    private readonly eventParams: IEventRequestParams;
    private readonly subdomain: string | null;
    public readonly onCancel: () => void;
    public fetchEventsList: () => Promise<void>;
    public isEventTrackerForm = false;
    public closeAddLinksPopup: () => void;

    @action.bound
    private async initLinkTypes(): Promise<void> {
        try {
            const response = await this.eventLinksDAO.getEventLinkTypes(this.eventParams);
            this.setEventLinkTypes(response);
        } catch (e) {
            EventLinksViewModel.handleApiException(e);
        }
    }

    @action.bound
    private async createEventLink(eventLink: CreateEventLinkType): Promise<void> {
        try {
            await this.eventLinksDAO.createEventLink(eventLink, this.eventParams);
            if (!this.isEventTrackerForm) await this.initEventLinks();

            this.trackingService.get().track(TRACKING_EVENTS.ADD_LINKS, {
                'Data saved': convertBoolTypeForMixPanel(true),
            });
        } catch (e) {
            EventLinksViewModel.handleApiException(e);
        }
    }

    @action.bound
    private async editEventLink(linkId: number, eventLink: CreateEventLinkType): Promise<void> {
        try {
            await this.eventLinksDAO.editEventLink(linkId, eventLink, this.eventParams);
            await this.initEventLinks();
        } catch (e) {
            EventLinksViewModel.handleApiException(e);
        }
    }

    private trackCancelEventLink(): void {
        try {
            this.trackingService.get().track(TRACKING_EVENTS.ADD_LINKS, {
                'Data saved': convertBoolTypeForMixPanel(false),
            });
        } catch (e) {
            EventLinksViewModel.handleApiException(e);
        }
    }

    @action.bound
    public onDeleteLink = (link: IEventLinkDTO) => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.userGroups.deleteThisLink'
                            description='Confirm message'
                            defaultMessage='delete the {label} link'
                            values={{ label: link.label || link.type }}
                        />
                    </span>
                    ?
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.userGroups.deleteLink'
                        description='label text'
                        defaultMessage='Delete Link'
                    />
                ),
                modifier: 'confirm-warn',
                handler: () => this.removeEventLink(link.linkId),
            },
            cancelButtonProps: {
                modifier: 'cancel',
            },
        });
    };

    @action.bound
    private async removeEventLink(linkId: number): Promise<void> {
        try {
            await this.eventLinksDAO.deleteEventLink(linkId, this.eventParams);
            await this.initEventLinks();
        } catch (e) {
            EventLinksViewModel.handleApiException(e);
        }
    }

    @action.bound
    private async initEventLinks(): Promise<void> {
        try {
            const response = await this.eventLinksDAO.getEventLinks(this.eventParams);
            this.setEventLinks(response);
        } catch (e) {
            EventLinksViewModel.handleApiException(e);
        }
    }
}
